import PropTypes from 'prop-types';
import cx from "ramda-extension/es/cx";
import { Children, cloneElement, createElement, forwardRef } from 'react';
import rejectNilChildren from '../../../utils/rejectNilChildren';
import { useDefaultComponentValue } from '../../../utils/useDefaultComponentValue';
const COMPONENT_NAME = 'ListBox';
const divElementType = 'div';
const ulElementType = 'ul';
const ListBox = /*#__PURE__*/ forwardRef((inProps, ref)=>{
    const props = useDefaultComponentValue({
        inProps,
        name: COMPONENT_NAME
    });
    const { className, showBorder = true, containerElement, children, ...other } = props;
    /**
	 * Function that tries to find out if any of the ListBoxItems
	 * has link property. If so, all container types are changed to 'div'.
	 * And the change is propagated to all children.
	 */ const getDerivedContainerElement = ()=>{
        const rejectedChildren = rejectNilChildren(children);
        let listType;
        if (rejectedChildren && !containerElement) {
            if (rejectedChildren.length > 0) {
                const linkItems = rejectedChildren.filter((ch)=>ch.props.link !== undefined);
                if (linkItems && linkItems.length > 0) {
                    listType = divElementType;
                }
            } else {
                if (rejectedChildren.link) {
                    listType = divElementType;
                }
            }
        }
        return listType ? listType : containerElement;
    };
    /**
	 * Propagation of container element and other props to children, when necessary.
	 */ const getDecoratedChildrens = (derivedContainer)=>{
        const rejectedChildren = rejectNilChildren(children);
        return Children.map(rejectedChildren, (child, index)=>{
            const childProps = {
                ...child.props,
                containerElement: derivedContainer ? derivedContainer : child.props.containerElement,
                key: index
            };
            return /*#__PURE__*/ cloneElement(child, childProps);
        });
    };
    const listClassName = cx('List', className, {
        'List--noBorder': !showBorder
    });
    const elementProps = {
        className: listClassName,
        ref,
        ...other
    };
    const container = getDerivedContainerElement();
    const decoratedChildrens = getDecoratedChildrens(container);
    const resultContainer = container ? container : ulElementType;
    const listElement = /*#__PURE__*/ createElement(resultContainer, elementProps, decoratedChildrens);
    return listElement;
});
ListBox.displayName = 'ListBox';
ListBox.propTypes = {
    /**
	 * Children will be rendered within the `ListBox`.
	 */ children: PropTypes.node,
    /**
	 * Custom `className` for the root.
	 */ className: PropTypes.string,
    /**
	 * Specifies container element of list and it`s items. Could be 'div' or 'span'.
	 */ containerElement: PropTypes.node,
    /**
	 * If true, the border for each item is shown.
	 */ showBorder: PropTypes.bool
};
export default ListBox;
